/* eslint-disable max-lines */
import {
  Component, OnInit, Input, Inject, ViewEncapsulation, ChangeDetectorRef
} from '@angular/core';
import { ViewportScroller } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { UntypedFormGroup } from '@angular/forms';
import { zip, Observable } from 'rxjs';
import { distinctUntilChanged, map, tap } from 'rxjs/operators';
import isEmpty from 'lodash-es/isEmpty';

import { IS_BROWSER } from '@app/modules/core/tokens/app.tokens';
import { AirValidationResponse, AirValidationResponseVm } from '@app/entities/air-validation.response';
import { PassengerArrangement } from '@shared/base-search-bar/entities/passenger-arrangement';
import { TravelDetailProvider } from '@shared/flight-itinerary/entities/travel-detail-provider';
import { BookingFacade } from '@booking/booking.facade';
import { TravelBookingType } from '@pax/entities/travel-booking-types';
import { TravelerTypes } from '@pax/entities/traveler-info';
import { BillingFacade } from '@billing/billing.facade';
import { PaxFacade } from '@pax/pax.facade';
import { PaxFormService } from '@pax/services/pax-form.service';
import { ScrollFormService } from '@reservation/services/scroll-form.service';
import { PropertiesAmplitudeService } from '@app/services/properties-amplitude.service';
import { BookingAmplitudeService } from '@booking/services/booking-amplitude.service';
import { TriggerAmplitudeService } from '@amplitude/services/trigger-amplitude.service';
import { AppFacade } from '@app/app.facade';
import { informativeBookingRoute, informativePaxCelebrateBookingRoute
} from '@booking/constants/booking-routes.constants';
import { AssistanceFacade } from '@shared/travel-assistance/assistance.facade';
import { FullValidTravelersPipe } from '@pax/pipes/full-valid-travelers.pipe';
import { AvoService } from '@avo/services/avo.service';
import { TravelerListVmPipe } from '@pax/pipes/traveler-list-vm.pipe';
import { BookingFlightsBaseContainer } from '@booking/abstracts/booking-flights-base.container';
import { SearchBy } from '@shared/base-search-bar/entities/search.entities';

@Component({
  selector: 'app-booking-buses',
  templateUrl: './booking-buses.container.html',
  styleUrls: ['../booking-base.container.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
  standalone: false
})
export class BookingBusesContainer extends BookingFlightsBaseContainer implements OnInit {

  @Input() airValidation: AirValidationResponse;

  public bookingFlightsForm: UntypedFormGroup;
  public busesSummaryProvider: TravelDetailProvider = TravelDetailProvider.BOOKING_BUSES;
  public adults: TravelerTypes = TravelerTypes.ADULTS;
  public policiesType: SearchBy = SearchBy.BUSES;

  constructor(
    protected route: ActivatedRoute,
    protected router: Router,
    protected cdRef: ChangeDetectorRef,
    protected appFacade: AppFacade,
    protected facade: BookingFacade,
    protected viewportScroller: ViewportScroller,
    protected billingFacade: BillingFacade,
    protected paxFacade: PaxFacade,
    protected paxFormService: PaxFormService,
    protected scrollFormService: ScrollFormService,
    protected propertiesAmplitudeService: PropertiesAmplitudeService,
    protected bookingAmplitudeService: BookingAmplitudeService,
    protected triggerAmplitudeService: TriggerAmplitudeService,
    protected assistanceFacade: AssistanceFacade,
    protected fullValidTravelersPipe: FullValidTravelersPipe,
    protected avoService: AvoService,
    protected travelerListVmPipe: TravelerListVmPipe,
    @Inject(IS_BROWSER) public isBrowser: boolean
  ) {
    super(
      route, router, cdRef, appFacade, facade, viewportScroller, billingFacade, paxFacade, 
      paxFormService, scrollFormService, propertiesAmplitudeService, bookingAmplitudeService, triggerAmplitudeService,
      assistanceFacade, fullValidTravelersPipe, avoService, travelerListVmPipe
    );
    this.travelerBookingProvider = TravelBookingType.BUSES;
  }

  ngOnInit(): void {
    this.mappedValidationID = this.airValidation?.ValidationId;
    if (this.isBrowser) {
      super.ngOnInit();
      this.amplitudeValidationResponse = this.airValidation;
      this.subscriptions.push(
        this.celebratePaxView$.pipe(
          distinctUntilChanged(),
          tap(celebratePaxView => {
            if (celebratePaxView) {
              this.router.navigate([{ outlets: { [informativeBookingRoute]: [informativePaxCelebrateBookingRoute] } }], {
                state: { celebratePaxView },
                relativeTo: this.route, skipLocationChange: true
              });
            }
          })
        ).subscribe()
      );
      this.configDefaultByInfoType(this.travelerBookingProvider, this.startDate);
      this.listenToAssistanceSearchCall(
        this.airValidation, this.airValidation?.TripOption?.Price?.CurrencyCode, this.route.snapshot.paramMap
      );
    }
  }

  get passengerArrangementsState$(): Observable<PassengerArrangement[]> {
    return this.facade.passengerArrangementsState$;
  }

  get hasairValidation(): boolean {
    return !isEmpty(this.airValidation);
  }

  get startDate(): string {
    return this.airValidation?.TripOption?.Sectors?.[0]?.Flights?.[0]?.DepartureDate;
  }

  public passengerInfo$(type: string): Observable<number[]> {
    return this.passengerArrangementsState$.pipe(
      map(arrangement => new Array(arrangement.find(passenger => passenger.id === type).quantity).fill(0)));
  }

  public showBusesDetail(data: { validator: AirValidationResponseVm, sectorNumber: number }): void {
    zip(
      this.passengerArrangementsState$,
      this.flightClass$,
    ).subscribe(([passengerArrangementsState, flightClass]) => {
      this.router.navigate([this.bookingDetail],
        {
          state: {
            passengerArrangement: passengerArrangementsState,
            price: data.validator.TripOption.Price,
            flightClass: flightClass,
            provider: data.validator.TripOption.Provider,
            airValidationSector: data.validator.TripOption.Sectors,
            travelType: this.travelerBookingProvider,
            sectorSelected: data.sectorNumber,
            productReference: data.validator.TripOption.ProductReference,
          },
          relativeTo: this.route, skipLocationChange: true
        }
      );
    }).unsubscribe();
  }
}
